'use client';

import React, { useEffect } from 'react';
import { cn } from '@/lib/cn';
import { IconAlertCircleFilled, IconCheck } from '@repo/svgs';

export type SnackbarVariant = 'alert' | 'success' | 'inverted';

export type SnackbarProps = {
  message: string;
  variant: SnackbarVariant;
  /** Auto-hide after N ms. Pass 0 to disable. */
  autoHideMs?: number;
  onClose?: () => void;
  className?: string;
};

export function Snackbar({
  message,
  variant,
  autoHideMs = 3000,
  onClose,
  className,
}: SnackbarProps) {
  useEffect(() => {
    if (!autoHideMs || autoHideMs <= 0) return;
    const t = window.setTimeout(() => onClose?.(), autoHideMs);
    return () => window.clearTimeout(t);
  }, [autoHideMs, onClose]);

  const bg =
    variant === 'alert'
      ? 'bg-alert'
      : variant === 'success'
        ? 'bg-success'
        : 'bg-inverted-secondary';

  return (
    <div
      className={cn(
        'fixed left-1/2 -translate-x-1/2 z-[60] bottom-24 w-[calc(100%-32px)] max-w-[520px]',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <div className={cn('flex items-center gap-3 rounded-2xl p-3 text-white shadow-lg', bg)}>
        {variant === 'success' ? (
          <IconCheck className="h-4 w-4 icon-inverted shrink-0" />
        ) : (
          <IconAlertCircleFilled className="h-4 w-4 icon-inverted shrink-0" />
        )}
        <div className="text-body-2 flex-1">{message}</div>
      </div>
    </div>
  );
}

export default Snackbar;
